import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ShieldCheck, Receipt, Truck, Sparkles, ArrowRight, Check } from "lucide-react";
import Layout from "../components/layout/Layout";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api/client";

const FEATURES = [
    { icon: Receipt, title: "Wholesale pricing", body: "5–15% off retail across all SKUs. Tiered by volume + organisation type." },
    { icon: ShieldCheck, title: "Net-15 / Net-30 credit", body: "Order today, pay later. Custom credit limits assigned on approval." },
    { icon: Truck, title: "Bulk dispatch", body: "Pan-India delivery with tracked logistics — Shiprocket-powered." },
    { icon: Sparkles, title: "GST invoicing", body: "CGST / SGST or IGST tax-compliant PDF invoices, ready for your CA." },
];

const STEPS = [
    "Submit your GSTIN + drug licence in 2 minutes.",
    "Our team verifies within 24 hours.",
    "Get a credit limit, start ordering at wholesale rates.",
];

const BusinessLanding = () => {
    const { user } = useAuth();
    const [account, setAccount] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!user) return;
        setLoading(true);
        api.get("/b2b/me").then(({ data }) => setAccount(data)).catch(() => {}).finally(() => setLoading(false));
    }, [user]);

    const cta = (() => {
        if (!user) return { to: "/login?next=/business", label: "Sign in to register", testId: "b2b-cta-signin" };
        if (!account && !loading) return { to: "/business/register", label: "Register your business", testId: "b2b-cta-register" };
        if (account?.status === "pending_approval") return { to: "/business/pending", label: "Application pending — view status", testId: "b2b-cta-pending" };
        if (account?.status === "approved") return { to: "/business/dashboard", label: "Open business dashboard", testId: "b2b-cta-dashboard" };
        if (account?.status === "rejected") return { to: "/business/register", label: "Update & resubmit application", testId: "b2b-cta-resubmit" };
        if (account?.status === "suspended") return { to: "/business/account", label: "Account suspended — contact support", testId: "b2b-cta-suspended" };
        return { to: "/business/register", label: "Register your business", testId: "b2b-cta-register" };
    })();

    return (
        <Layout>
            <div data-testid="business-landing">
                <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-violet-50/50">
                    <div className="mx-auto max-w-6xl px-6 py-20 lg:px-12 lg:py-28">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-700">
                            <Building2 className="h-3 w-3" /> PILLS · For Business
                        </span>
                        <h1 className="mt-4 text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl lg:text-6xl">
                            Wholesale pharma for <br />
                            <span className="text-brand-700">clinics, hospitals & labs.</span>
                        </h1>
                        <p className="mt-5 max-w-2xl text-lg text-zinc-600">
                            Open a B2B account in 24 hours. Order at 5–15% off retail with Net-15 or Net-30 credit terms. GST-compliant invoicing built in.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link to={cta.to} className="btn-brand !text-base !px-6 !py-3" data-testid={cta.testId}>
                                {cta.label} <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link to="/products" className="rounded-full border border-zinc-200 px-5 py-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
                                Browse retail catalogue →
                            </Link>
                        </div>
                    </div>
                </section>

                <section className="mx-auto max-w-6xl px-6 py-20 lg:px-12">
                    <p className="label-uppercase">Why PILLS for business</p>
                    <h2 className="mt-2 text-3xl font-semibold tracking-tight text-zinc-900">Built for procurement, by pharmacists.</h2>
                    <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-soft" data-testid={`b2b-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
                                <f.icon className="h-6 w-6 text-brand-700" />
                                <p className="mt-3 text-base font-semibold text-zinc-900">{f.title}</p>
                                <p className="mt-1 text-sm text-zinc-500">{f.body}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="bg-zinc-50">
                    <div className="mx-auto max-w-4xl px-6 py-16 lg:px-12">
                        <p className="label-uppercase">How it works</p>
                        <h2 className="mt-2 text-3xl font-semibold tracking-tight text-zinc-900">Three steps to wholesale.</h2>
                        <ol className="mt-8 space-y-4">
                            {STEPS.map((s, i) => (
                                <li key={i} className="flex items-start gap-3" data-testid={`b2b-step-${i + 1}`}>
                                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-700 text-xs font-bold text-white">
                                        {i + 1}
                                    </span>
                                    <p className="text-base text-zinc-800">{s}</p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                <section className="mx-auto max-w-3xl px-6 py-20 text-center lg:px-12">
                    <h3 className="text-2xl font-semibold tracking-tight text-zinc-900">Ready to start?</h3>
                    <p className="mx-auto mt-2 max-w-xl text-zinc-500">
                        Pricing tiers are assigned on approval based on your organisation type and projected volume.
                    </p>
                    <Link to={cta.to} className="btn-brand mt-6 !text-base !px-6 !py-3" data-testid="b2b-cta-bottom">
                        {cta.label} <ArrowRight className="h-4 w-4" />
                    </Link>
                </section>
            </div>
        </Layout>
    );
};

export default BusinessLanding;
